/**
 * The app's one source for the bell, share and star glyphs and for the round
 * icon button they sit in (ISSUE-1085 R6), so a score row's bell and the game
 * header's bell can never drift apart. The share glyph is the three node
 * glyph, because the owner rejected the legacy arrow out of a box on
 * 2026-09-20 (ISSUE-952 R10). `ICON_BUTTON_CLASS` is the 44px round button
 * every header and row icon uses: one `touchTarget` square, pinned in pixels
 * so it does not double at a larger root font size, carrying the shared focus
 * ring.
 */
import { cn, FOCUS_RING } from "@sportsmet/ui";
import type { ReactNode } from "react";

export const ICON_BUTTON_CLASS = cn(
  "inline-flex h-touch-target w-touch-target shrink-0 items-center justify-center rounded-full",
  FOCUS_RING,
);

/** The bell, outlined, or filled when the fan has alerts on (ISSUE-1391). */
export function BellIcon(props: { isFilled?: boolean } = {}): ReactNode {
  return (
    <svg
      className="h-icon-md w-icon-md"
      viewBox="0 0 24 24"
      fill={props.isFilled === true ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-icon="bell"
      data-filled={props.isFilled === true ? "true" : undefined}
    >
      <path d="M12 3a6 6 0 0 0-6 6v4l-2 3h16l-2-3V9a6 6 0 0 0-6-6z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </svg>
  );
}

export function ShareIcon(props: { testId?: string }): ReactNode {
  return (
    <svg
      data-testid={props.testId}
      data-icon="share"
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-icon-md w-icon-md"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.6" y1="10.5" x2="15.4" y2="6.5" />
      <line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
    </svg>
  );
}

/** The star outline, shared with the score row's inline favorite star in
 *  `glyphs.tsx`, so the two can never drift apart. */
export const STAR_PATH =
  "M12 2.5l2.9 6.16 6.6.79-4.9 4.5 1.29 6.55L12 17.02l-5.89 3.48L7.4 13.95l-4.9-4.5 6.6-.79z";

export function StarIcon(props: { isFilled: boolean }): ReactNode {
  return (
    <svg
      className="h-icon-md w-icon-md"
      viewBox="0 0 24 24"
      fill={props.isFilled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-icon="star"
    >
      <path d={STAR_PATH} />
    </svg>
  );
}

/** Three dots, for a card's More options menu (ISSUE-1344). */
export function MoreIcon(): ReactNode {
  return (
    <svg
      data-icon="more"
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-icon-md w-icon-md"
      fill="currentColor"
    >
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}
